import { Input } from 'antd';
import { useState } from 'react';

import { ImageAttach, type AttachedImage } from '@/components/common/image-attach';
import { Select } from '@/components/common/select';
import { useConfirm } from '@/components/feedback/dialog';
import { Modal } from '@/components/feedback/modal';
import { useToast } from '@/components/feedback/toast';
import type { Device } from '@/features/devices';
import { ApiError } from '@/lib/api/errors';

import {
  DESC_MAX,
  DESC_MIN,
  SUBSCRIPTION_CATEGORIES,
  SUPPORT_ERROR_CODES,
  TITLE_MAX,
} from '../constants';
import { useCreateSupportTicket } from '../hooks';
import type { SupportTicket } from '../types';

import styles from './raise-support-ticket-modal.module.scss';

export type RaiseSupportTicketModalProps = {
  open: boolean;
  /** Fixed device (device-detail entry path) — read-only when provided. */
  device?: Device;
  /** Selectable devices (profile entry path) — used when `device` is omitted. */
  devices?: Device[];
  onClose: () => void;
  /** Fired with the created ticket so the flow can show the success dialog. */
  onSubmitted: (ticket: SupportTicket) => void;
  /** Fired when the backend reports the open-ticket limit (AC §7). */
  onLimitReached: () => void;
  /** Fired when the backend rejects a duplicate submission (AC §8). */
  onDuplicate: () => void;
};

function Label({ children, required }: { children: string; required?: boolean }) {
  return (
    <span className={styles.label}>
      {children}
      {required ? <span className={styles.req}> *</span> : null}
    </span>
  );
}

/**
 * Raise Support Ticket form (Figma 2249-675487 / 2249-678188 / 2614-374836).
 *
 * Device is pre-populated and read-only (device-detail entry path). Submit enables only
 * when Title is non-empty, Description is more than 20 chars, and all image uploads have
 * finished (AC §6) — Subscription Category is shown for design fidelity but does not gate
 * (informational, per product decision). Oversize image rejects surface the dark toast.
 */
export function RaiseSupportTicketModal({
  open,
  device,
  devices = [],
  onClose,
  onSubmitted,
  onLimitReached,
  onDuplicate,
}: RaiseSupportTicketModalProps) {
  const toast = useToast();
  const { confirm } = useConfirm();
  const create = useCreateSupportTicket();

  const fixed = device != null;
  const [selectedId, setSelectedId] = useState<string | null>(device?.id ?? null);
  const selectedDevice = fixed ? device : (devices.find((d) => d.id === selectedId) ?? null);

  const defaultCategory = selectedDevice
    ? (selectedDevice.subscription.planName ?? selectedDevice.name)
    : null;
  const categoryOptions = Array.from(
    new Set([defaultCategory, ...SUBSCRIPTION_CATEGORIES].filter(Boolean) as string[]),
  ).map((c) => ({ value: c, label: c }));

  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<string | null>(defaultCategory);
  const [description, setDescription] = useState('');
  const [images, setImages] = useState<AttachedImage[]>([]);
  const [titleTouched, setTitleTouched] = useState(false);
  const [descTouched, setDescTouched] = useState(false);

  // Reset to a fresh form whenever the modal (re)opens (AC §16 — no draft persistence).
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setTitle('');
      setSelectedId(device?.id ?? null);
      setCategory(defaultCategory);
      setDescription('');
      setImages([]);
      setTitleTouched(false);
      setDescTouched(false);
    }
  }

  const titleValid = title.trim().length >= 1;
  const descValid = description.trim().length > DESC_MIN;
  const uploading = images.some((img) => img.status === 'uploading');
  const valid = !!selectedDevice && titleValid && descValid && !uploading;
  const dirty = title.length > 0 || description.length > 0 || images.length > 0;

  const submit = () => {
    if (!valid || create.isPending) return;
    create.mutate(
      {
        deviceId: selectedDevice!.id,
        title: title.trim(),
        description: description.trim(),
        subscriptionCategory: category,
        images: images.map((img) => img.name),
      },
      {
        onSuccess: (ticket) => onSubmitted(ticket),
        onError: (err) => {
          const code = err instanceof ApiError ? err.code : undefined;
          if (code === SUPPORT_ERROR_CODES.openTicketLimit) {
            onLimitReached();
            return;
          }
          if (code === SUPPORT_ERROR_CODES.duplicate) {
            onDuplicate();
            return;
          }
          toast.error({ title: 'Unable to submit ticket', description: 'Please try again.' });
        },
      },
    );
  };

  // Discard guard (AC §10): confirm only when the form has user-entered data.
  const requestClose = () => {
    if (create.isPending) return;
    if (!dirty) {
      onClose();
      return;
    }
    void confirm({
      title: 'Discard Ticket?',
      subtitle: 'Your ticket details will be lost if you leave.',
      confirmText: 'Leave',
      cancelText: 'Stay',
      danger: true,
    }).then((leave) => {
      if (leave) onClose();
    });
  };

  return (
    <Modal
      open={open}
      onClose={requestClose}
      title="Raise Support Ticket"
      size="lg"
      loading={create.isPending}
      primaryText="Submit"
      primaryVariant="neutral"
      primaryDisabled={!valid}
      onPrimary={submit}
      cancelText="Cancel"
    >
      <div className={styles.form}>
        <div className={styles.field}>
          <Label required>Selected Device</Label>
          <Select
            disabled={fixed}
            value={selectedDevice?.id ?? null}
            placeholder="Select a device"
            options={
              fixed && device
                ? [{ value: device.id, label: device.name }]
                : devices.map((d) => ({ value: d.id, label: d.name }))
            }
            onChange={(value) => {
              const id = value as string;
              setSelectedId(id);
              const d = devices.find((x) => x.id === id);
              setCategory(d ? (d.subscription.planName ?? d.name) : null);
            }}
            aria-label="Selected device"
          />
        </div>

        <div className={styles.row2}>
          <label className={styles.field}>
            <Label required>Title</Label>
            <Input
              value={title}
              maxLength={TITLE_MAX}
              placeholder="Pump not Working"
              status={titleTouched && !titleValid ? 'error' : undefined}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => setTitleTouched(true)}
            />
            {titleTouched && !titleValid ? (
              <span className={styles.error}>Please enter a title</span>
            ) : null}
          </label>

          <div className={styles.field}>
            <Label required>Subscription Category</Label>
            <Select
              value={category}
              options={categoryOptions}
              placeholder="Select category"
              onChange={(value) => setCategory(value as string)}
              aria-label="Subscription category"
            />
          </div>
        </div>

        <label className={styles.field}>
          <Label>Description</Label>
          <Input.TextArea
            value={description}
            maxLength={DESC_MAX}
            rows={3}
            placeholder="Enter description"
            status={descTouched && description.length > 0 && !descValid ? 'error' : undefined}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={() => setDescTouched(true)}
          />
          {descTouched && description.length > 0 && !descValid ? (
            <span className={styles.error}>
              Description must be more than {DESC_MIN} characters
            </span>
          ) : null}
        </label>

        <ImageAttach value={images} onChange={setImages} />
      </div>
    </Modal>
  );
}
